import type { FC } from 'react';
import { lazy, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuctionTiles } from 'src/apis/monolith/use-auction-tiles';
import { useOrganisationServicePublic } from 'src/apis/organisation-service/use-organisation-service-public';
import { usePublicOperators } from 'src/apis/organisation-service/use-public-operators';
import { useAuctionTiles as useAuctionTilesTAS } from 'src/apis/transport-auction/use-auction-tiles';
import { useOptionalTransportAuctionServiceRoot } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { useOptionalTtpcModulationService } from 'src/apis/ttpc-modulation-service/use-ttpc-modulation-service';
import { PageSpinner } from 'src/components/spinner-container';
import { useCognitoUser } from 'src/hooks/use-cognito-user';
import { useOptionalDashboardNews } from 'src/hooks/use-dashboard-news';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useOptionalBidCollectorRoot } from 'src/pages/snam-bid-collector/use-bid-collector';

const Page = lazy(() => import('./page'));

export const Start: FC = () => {
  const cognitoUser = useCognitoUser();
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  if (monolithUser?.isTso) return <RedirectToAngularDashboard />;
  if (cognitoUser) return <RedirectToGenericDashboard />;
  return <Guest />;
};

const RedirectToAngularDashboard: FC = () => {
  const navigate = useNavigate();
  useLayoutEffect(() => {
    navigate('/platform/#/start', { replace: true });
    // oxlint-disable-next-line exhaustive-deps
  }, []);
  return <PageSpinner />;
};

const RedirectToGenericDashboard: FC = () => {
  const navigate = useNavigate();
  useLayoutEffect(() => {
    navigate('/dashboard', { replace: true });
    // oxlint-disable-next-line exhaustive-deps
  }, []);
  return <PageSpinner />;
};

const Guest: FC = () => {
  const auctionTiles = useAuctionTiles();

  const transportAuctionServiceRoot = useOptionalTransportAuctionServiceRoot();
  const auctionTilesTAS = useAuctionTilesTAS({
    root: transportAuctionServiceRoot,
  });

  const snamBidCollector = useOptionalBidCollectorRoot();

  const ttpcModulationService = useOptionalTtpcModulationService();

  const organisationServicePublic = useOrganisationServicePublic();
  const operators = usePublicOperators({
    randomOrder: true,
    organisationServicePublic,
  });

  const dashboardNews = useOptionalDashboardNews();

  if (
    transportAuctionServiceRoot?.loaded === false ||
    auctionTilesTAS?.response === null ||
    !auctionTiles.response ||
    !snamBidCollector.loaded ||
    !operators.response ||
    (PRISMA_CONFIG.ttpcModulationService && !ttpcModulationService.loaded) ||
    dashboardNews?.loaded === false
  )
    return <PageSpinner />;

  return (
    <Page
      auctionTiles={auctionTiles}
      auctionTilesTAS={auctionTilesTAS}
      snamBidCollector={snamBidCollector}
      operators={operators}
      ttpcModulationService={ttpcModulationService}
      dashboardNews={dashboardNews}
    />
  );
};
